'use client';
import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown, RotateCcw } from 'lucide-react';
import { LANGUAGES, TEST_DURATIONS } from '@/data/wordLists';
import { useTestConfig } from '@/context/TestConfigContext';

interface StageControlsProps {
  onRestart: () => void;
}

/**
 * The test controls that live directly on the stage: a segmented duration
 * toggle, a language picker, and a restart button. Wrapped in
 * data-test-config-controls so the typing-focus guard treats interactions here
 * as intentional (never popping the blur overlay), and emits the config
 * open/close events the client listens to for refocus behaviour.
 */
export default function StageControls({ onRestart }: StageControlsProps) {
  const { language, duration, setLanguage, setDuration } = useTestConfig();
  const [langOpen, setLangOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const currentLang = LANGUAGES.find((l) => l.code === language)!;

  const emitOpen = () => document.dispatchEvent(new CustomEvent('typeswift-config-open'));
  const emitClose = (detail: { refocus?: boolean; target?: EventTarget | null } = { refocus: true }) =>
    document.dispatchEvent(new CustomEvent('typeswift-config-close', { detail }));

  const closeLang = (detail: { refocus?: boolean; target?: EventTarget | null } = { refocus: true }) => {
    setLangOpen(false);
    emitClose(detail);
  };

  useEffect(() => {
    if (!langOpen) return;
    const onPointerDown = (e: MouseEvent | TouchEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        closeLang({ refocus: false, target: e.target });
      }
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeLang({ refocus: true });
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [langOpen]);

  return (
    <div ref={containerRef} data-test-config-controls className="flex items-center gap-2.5 flex-wrap">
      {/* Duration segmented control */}
      <div className="seg" role="group" aria-label="Test duration">
        {TEST_DURATIONS.map((d) => (
          <button
            key={`seg-${d.value}`}
            type="button"
            onClick={() => setDuration(d.value)}
            className={`seg-btn ${d.value === duration ? 'active' : ''}`}
            aria-pressed={d.value === duration}
          >
            {d.label}
          </button>
        ))}
      </div>

      {/* Language picker */}
      <div className="relative">
        <button
          type="button"
          className="stage-chip"
          aria-haspopup="listbox"
          aria-expanded={langOpen}
          onClick={() => {
            if (langOpen) closeLang({ refocus: true });
            else { setLangOpen(true); emitOpen(); }
          }}
        >
          <span>{currentLang.flag}</span>
          <span className="hidden sm:inline">{currentLang.label}</span>
          <ChevronDown size={12} className={`text-faint transition-transform ${langOpen ? 'rotate-180' : ''}`} />
        </button>

        {langOpen && (
          <div className="layer-float absolute left-0 top-full mt-1.5 w-60 overflow-hidden z-50 py-1" role="listbox">
            {LANGUAGES.map((lang) => {
              const isSelected = lang.code === language;
              return (
                <button
                  key={`lang-${lang.code}`}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  onClick={() => { setLanguage(lang.code); closeLang({ refocus: true }); }}
                  className="w-full flex items-center gap-3 px-3.5 py-2 text-sm text-left transition-colors hover:bg-muted"
                  style={{
                    color: 'var(--foreground)',
                    fontWeight: isSelected ? 600 : 400,
                  }}
                >
                  <span>{lang.flag}</span>
                  <span>{lang.label}</span>
                  <span className="ml-auto text-xs text-faint">{lang.nativeLabel}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Restart */}
      <button
        type="button"
        onClick={onRestart}
        className="stage-restart"
        aria-label="New test"
        title="New test (Tab)"
      >
        <RotateCcw size={16} />
      </button>
    </div>
  );
}
